import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  getAvailabilityStoresSuccess,
  toggleGetAvailabilityStoresLoading
} from '@redux/actions/bopis/getAvailabilityStores';
import { GET_AVAILABILITY_STORES_TYPE } from '@utils/constant';

import useFetchAvailabilityStores from './useFetchAvailabilityStores';

/** INFO: using isCallingAPI to prevent duplicate API call when using component in multiple place  */
let isCallingAPI = false;

const useFetchAvailabilityStoresByProductsList = () => {
  const dispatch = useDispatch();
  const fetchAvailabilityStores = useFetchAvailabilityStores();

  return useCallback(
    /** @param {Array} productsList
     * @param {Number} type */
    async (productsList, type = GET_AVAILABILITY_STORES_TYPE.Or) => {
      let availabilityStores = [];
      if (!isCallingAPI) {
        for (const productsListItem of productsList) {
          dispatch(toggleGetAvailabilityStoresLoading(true));
          try {
            isCallingAPI = true;

            const fetchAvailabilityStoresResponse = await fetchAvailabilityStores({
              ListProducts: productsListItem,
              Type: type
            });

            if (fetchAvailabilityStoresResponse?.ListAvailablityStores?.length) {
              availabilityStores = [...availabilityStores, ...fetchAvailabilityStoresResponse.ListAvailablityStores];
            }
          } catch (error) {
            console.error('Failed to fetch availability stores', error);
          } finally {
            isCallingAPI = false;

            dispatch(toggleGetAvailabilityStoresLoading(false));
          }
        }

      }

      return availabilityStores;
    }, []);
};

const useGetAvailabilityStores = () => {
  const dispatch = useDispatch();
  const fetchAvailabilityStores = useFetchAvailabilityStoresByProductsList();

  return useCallback(
    /** @param {Object} payload
     * @param {Boolean} payload.isEnableBOPISOption
     * @param {Array} payload.productsList
     * @param {Array} [payload.freeGiftsList]
     * @param {Array} [payload.additionalOptions]
     * @param {Number} type*/
    async ({
      isEnableBOPISOption,
      productsList = [],
      freeGiftsList = [],
      additionalOptions = []
    }, type = GET_AVAILABILITY_STORES_TYPE.Or) => {
      if (isEnableBOPISOption && productsList.length) {
        let availabilityStores = [];

        /** INFO: main product handler */
        const availabilityStoreForMainProducts = await fetchAvailabilityStores(
          productsList,
          type
        );
        if (availabilityStoreForMainProducts.length) {
          availabilityStores = [...availabilityStores, ...availabilityStoreForMainProducts];
        }

        /** INFO: free-gift handler */
        if (freeGiftsList.length) {
          const availabilityStoreForFreeGift = await fetchAvailabilityStores(
            [freeGiftsList],
            GET_AVAILABILITY_STORES_TYPE.Or
          );

          availabilityStores = [...availabilityStores, ...availabilityStoreForFreeGift];
        }

        /** INFO: additional options handler */
        if (additionalOptions.length) {
          const availabilityStoreForAdditionalOption = await fetchAvailabilityStores(
            additionalOptions,
            GET_AVAILABILITY_STORES_TYPE.Or
          );

          availabilityStores = [...availabilityStores, ...availabilityStoreForAdditionalOption];
        }

        /** INFO: merge stores after fetching stores finish */
        if (availabilityStores.length) {
          /** INFO: productsList.length + freeGiftsList.length ? 1 : 0 + additionalOptions.length || 0 to ensure store available for product, free-gift and additional options */
          const result = [...availabilityStores].reduce((unique, item) => {
            if ((
              availabilityStores.filter(storeObject => storeObject.OutletId === item.OutletId).length >= (
                productsList.length + (
                  freeGiftsList.length ? 1 : 0
                ) + (
                  additionalOptions.length || 0
                )
              )
            ) && !unique.find(storeObject => storeObject.OutletId === item.OutletId)) {
              unique.push(item);
            }

            return unique;
          }, []);

          dispatch(getAvailabilityStoresSuccess({
            ListAvailablityStores: result
          }));
        } else {
          dispatch(getAvailabilityStoresSuccess({
            ListAvailablityStores: []
          }));
        }
      }

      return [];
    }, []);
};

export default useGetAvailabilityStores;